import type { MyTrip } from '@/store/useMyVehicleTripsStore';
import { formatUtcToLocalTime } from '@/utils/dateUtils';
import { DRIVER, PRIVATE, isTripRunning } from '@/utils/vehicleTrips';
import type { TFn } from './timeClockFormat';

/**
 * "Fahrt W 481 GL 08:10–09:20" or "Mitfahrt …" (`timeclock:trips.*`) — a running trip ends with
 * "…", one the administration closed without arrival shows its departure only.
 */
export function describeTripSpan(trip: MyTrip, t: TFn): string {
  const start = formatUtcToLocalTime(trip.startedAt);
  const end = trip.endedAt ? formatUtcToLocalTime(trip.endedAt) : isTripRunning(trip) ? '…' : null;
  const span = end ? `${start}–${end}` : start;
  return t(trip.role === DRIVER ? 'trips.lineTrip' : 'trips.lineRide', { plate: trip.licensePlate, span });
}

/** "Firma → Objekt Müller" of an own trip; a ride has no route of its own (''). */
export function describeTripRoute(trip: MyTrip): string {
  return trip.role === DRIVER ? [trip.startLocation, trip.destination].map((p) => p || '–').join(' → ') : '';
}

/** Purpose of an own trip, "Privat" for a private one; '' for a ride. */
export function describeTripPurpose(trip: MyTrip, t: TFn): string {
  if (trip.role !== DRIVER) return '';
  return trip.purposeKind === PRIVATE ? t('vehicles:trip.kind.private') : (trip.purpose ?? '');
}

/**
 * "Mitfahrer: …" for an own trip, "Fahrer: …" for a ride — for a ride the API lists the drivers
 * of the trips it overlapped in `coTravellers`. '' when nobody is known.
 */
export function describeTripPeople(trip: MyTrip, t: TFn): string {
  const names = (trip.coTravellers ?? []).join(', ');
  if (!names) return '';
  return t(trip.role === DRIVER ? 'trips.coTravellersLine' : 'trips.driversLine', { names });
}
